import type { Dispatch, SetStateAction } from "react";
import { SR_VOICE_DEFAULT_MIME, SR_VOICE_PLACEHOLDER } from "../sr-constants";
import { blobToBase64 } from "./sr-blob-to-base64";
import {
    type SrChatMessage,
    appendAssistant,
    chartsFromResponse,
    nameCandidatesFromResponse,
    toHistory,
} from "./sr-chat-types";

type ApiPost = (payload: Record<string, unknown>) => Promise<any>;

export const runVoiceSend = async (opts: {
    blob: Blob;
    mime?: string;
    projectid: string;
    messages: SrChatMessage[];
    conversationId?: string;
    postTranscribe: ApiPost;
    postChat: ApiPost;
    setMessages: Dispatch<SetStateAction<SrChatMessage[]>>;
}) => {
    const {
        blob,
        mime,
        projectid,
        messages,
        conversationId,
        postTranscribe,
        postChat,
        setMessages,
    } = opts;
    const pid = parseInt(projectid, 10);
    const userId = `u-${Date.now()}`;
    const audio_mime = (mime || SR_VOICE_DEFAULT_MIME).split(";")[0];

    setMessages((prev) => [
        ...prev,
        { id: userId, role: "user", content: SR_VOICE_PLACEHOLDER },
    ]);

    let transcript = "";
    try {
        const audio_base64 = await blobToBase64(blob);
        const stt = await postTranscribe({
            projectid: pid,
            audio_base64,
            audio_mime,
        });
        transcript = (stt?.transcript || "").trim();
        if (!transcript) {
            throw new Error("Empty transcript from Whisper");
        }
        // Show transcribed text in chat before DeepSeek
        setMessages((prev) =>
            prev.map((m) =>
                m.id === userId ? { ...m, content: transcript } : m
            )
        );
    } catch (err: any) {
        setMessages((prev) =>
            prev.map((m) =>
                m.id === userId
                    ? { ...m, content: "🎤 (could not transcribe)" }
                    : m
            )
        );
        appendAssistant(
            setMessages,
            err?.message || "Voice transcription failed. Please try again."
        );
        return;
    }

    try {
        const data = await postChat({
            projectid: pid,
            message: transcript,
            history: toHistory(messages),
            conversation_id: conversationId,
            debug: false,
        });
        appendAssistant(
            setMessages,
            data?.reply?.trim() || "No reply received from the agent.",
            chartsFromResponse(data),
            nameCandidatesFromResponse(data)
        );
    } catch (err: any) {
        appendAssistant(
            setMessages,
            err?.message || "Something went wrong talking to the SR agent."
        );
    }
};
